import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Input } from "antd";

import { cn } from "~/core/generic/shadcn-utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandList,
  CommandLoading,
} from "~/core/ui/Command";
import If from "~/core/ui/If";
import Spinner from "~/core/ui/Spinner";

import { faChevronDown, faSearch } from "@fortawesome/pro-regular-svg-icons";

import { useDefaultDDRenderer } from "../../hooks/useDefaultDDRenderer";
import { DDRendererProps } from "../../types";
import { StyledScrollIndicator } from "../Styled/StyledScrollIndicator";

import { DDRendererCommandListItem } from "./DDRendererCommandListItem";

export const DefaultDDRenderer = (props: DDRendererProps<any>) => {
  const {
    localItems,
    inputRef,
    inputValue,
    handleInputChange,
    handleItemSelect,
    showScrollIndicator,
    commandListRef,
    handleScroll,
    selectedValue,
    setSelectedValue,
  } = useDefaultDDRenderer(props);

  // Handle keyboard events at the Command level
  const handleCommandKeyDown = (e: React.KeyboardEvent) => {
    // Handle original keydown events from props
    props.onCommandKeydown?.(e);

    // Handle Enter key
    if (e.key === "Enter" && selectedValue) {
      const selectedItem = localItems.find(
        item => item.id.toString() === selectedValue,
      );
      if (selectedItem) {
        handleItemSelect(selectedItem);
        if (props.isMultiSelect) {
          props.handleMultiSelect && props.handleMultiSelect(selectedItem);
        }
      }
    }
  };

  return (
    <Command
      loop
      onKeyDown={handleCommandKeyDown}
      value={selectedValue}
      onValueChange={setSelectedValue}
      defaultValue={localItems?.[0]?.id.toString()}
    >
      <CommandGroup
        className={cn("p-2", {
          hidden: props.noSearch,
        })}
      >
        <Input
          ref={inputRef}
          value={inputValue}
          placeholder={props.placeholder}
          allowClear={props.allowInputClear}
          onChange={handleInputChange}
          aria-label="Search"
          prefix={<FontAwesomeIcon icon={faSearch} />}
          className="!shadow-none"
          autoFocus
          onFocus={e => {
            // Select all text when input is focused
            e.target.select();
          }}
          onKeyDown={e => {
            if (e.key === "Tab") {
              e.preventDefault();
              const selectedItem = localItems.find(
                item => item.id.toString() === selectedValue,
              );
              if (selectedItem) {
                handleItemSelect(selectedItem);
              }
            }
          }}
        />
      </CommandGroup>
      <CommandList ref={commandListRef} onScroll={handleScroll}>
        <CommandGroup
          className={cn("p-2 scrollanle", {
            "pt-0": !props.noSearch,
          })}
          aria-label="Search Results"
        >
          {localItems?.map(item => {
            const isSelected = item.id.toString() === selectedValue;
            return (
              <DDRendererCommandListItem
                item={item}
                isSelected={isSelected}
                isMultiSelect={props.isMultiSelect}
                onSelect={handleItemSelect}
                onMultiSelect={props.handleMultiSelect}
                key={item.id}
              />
            );
          })}
        </CommandGroup>
        <If condition={props.loading}>
          <CommandLoading>
            <Spinner />
          </CommandLoading>
        </If>
        <If condition={!props.loading && !props.emptyContent}>
          <CommandEmpty className="text-light text-center pb-5">
            No results found for{" "}
            <span className="font-medium text-darker text-sm">
              {inputValue}
            </span>
          </CommandEmpty>
        </If>
        <If condition={!props.loading && props.emptyContent}>
          <div className="text-light text-center pb-5">
            {props.emptyContent}
          </div>
        </If>
      </CommandList>
      {showScrollIndicator && (
        <StyledScrollIndicator>
          <FontAwesomeIcon icon={faChevronDown} className="size-3" />
        </StyledScrollIndicator>
      )}
    </Command>
  );
};
